/**
   Copyright (c)2021 3Key Company s.r.o., All Rights Reserved

   It is strictly prohibited to distribute the source code,
   transpiled and compiled code or its parts without previous
   agreement of the 3Key Company board.
*/

import "./Dialog.css";

import React from "react";

import { DialogProps, DialogState } from "./Dialog.types";
import { getScrollbarWidth } from "lib/getScrollBarWidth";

const openedDialogs: HTMLDivElement[] = [];
let topLevelDialog: React.Component | undefined;

export default class Dialog extends React.Component<DialogProps, DialogState> {

   private dialogRef: React.RefObject<HTMLDivElement>;

   private componentClassName: string;

   constructor(props: DialogProps) {

      super(props);

      this.componentClassName = "tk-dialog" + (this.props.componentName ? `-${this.props.componentName}` : "");

      this.dialogRef = React.createRef<HTMLDivElement>();

   }

   public componentDidMount(): void {

      if (this.props.topLevel && topLevelDialog !== undefined) {
         throw new Error("Only one top level dialog can be dispayed at one time!");
      } else {
         if (this.props.topLevel) topLevelDialog = this;
      }

      if (openedDialogs.length > 0) openedDialogs[openedDialogs.length - 1].setAttribute("data-disabled", "true");
      openedDialogs.push(this.dialogRef.current as HTMLDivElement);

      const pr = window.getComputedStyle(document.body).paddingRight;

      document.body.style.top = `-${window.scrollY}px`;
      document.body.style.position = "fixed";
      document.body.style.paddingRight = parseInt(pr) + getScrollbarWidth() + "px";

      window.addEventListener("keydown", this.onWindowKeyDown);

      const focusableElements = this.getFocusableElements();
      setTimeout(() => {if (focusableElements.length > 0) focusableElements[0].focus();}, 0);


   }

   public componentWillUnmount(): void {

      if (topLevelDialog === this) {
         topLevelDialog = undefined;
      }

      const scrollY = document.body.style.top;

      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.paddingRight = "";

      window.scrollTo(0, parseInt(scrollY || '0') * -1);

      window.removeEventListener("keydown", this.onWindowKeyDown);

      const index = openedDialogs.findIndex(od => od === this.dialogRef.current);
      openedDialogs.splice(index, 1);
      if (openedDialogs.length > 0) {
         openedDialogs[openedDialogs.length - 1].setAttribute("data-disabled", "false");
         const fels = this.getFocusableElements(openedDialogs[openedDialogs.length - 1]);
         if (fels.length > 0) fels[0].focus();
      }


   }

   public componentDidUpdate(prevProps: DialogProps, prevState: DialogState) {
   }

   public render(): React.ReactNode {

      return (

         <div
            ref={this.dialogRef}
            className={this.componentClassName}
            top-level={this.props.topLevel}
            onMouseDown={this.onOverlayMouseDown}
            aria-modal="true"
            role="dialog"

            data-disabled={false}
         >

            <div className="overlay">

               <a href="#focusKeeperFirst" className="focusKeeper" onFocus={(e) => this.focusKeeperFocus(e, "first")}>&nbsp;</a>

               <div className="container" onMouseDown={(e) => e.stopPropagation()}>

                  {this.props.children}

                  <div className="disabler">
                  </div>

               </div>

               <a href="#focusKeeperLast" className="focusKeeper" onFocus={(e) => this.focusKeeperFocus(e, "last")}>&nbsp;</a>

            </div>

         </div>

      )

   }

   private onOverlayMouseDown = (e?: React.MouseEvent) => {
      if (this.props.closeOnOverlayMouseDown && this.props.onClose) this.props.onClose();
   }

   private onWindowKeyDown = (event: KeyboardEvent) => {
     if (event.key === "Escape" && this.dialogRef.current?.getAttribute("data-disabled") === "false" && this.props.closeOnEscape && this.props.onClose) this.props.onClose();
   }

   private focusKeeperFocus = (e: React.FocusEvent, fl: "first" | "last") => {

      e.preventDefault();

      const filtered = this.getFocusableElements();
      if (filtered.length === 0) return;

      if (fl === "first") filtered[filtered.length - 1].focus();

      if (fl === "last") filtered[0].focus();

   }

   private getFocusableElements = (el: HTMLElement | null = this.dialogRef.current): HTMLElement[] => {

      if (!el) return [];

      const focusableElements = el.querySelectorAll("a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object:not([disabled]), embed, *[tabindex], *[contenteditable]");
      const filtered = Array.from(focusableElements).filter(e => e.className !== "focusKeeper");

      return filtered as HTMLElement[];

   }

}
